import { FormattedDate, FormattedMessage } from 'react-intl';
import { Box, Flex, Text } from '@theme-ui/components';
import Cookies from 'js-cookie';

import FormattedCountryName from '@app/components/FormattedCountryName';
import { dateSafariCompatible } from '@app/helpers/date';
import { COUNTRY_COOKIE } from '@app/helpers/localization';
import {
  Magento2_ProductStockStatus as STOCK_STATUS,
  type Magento2_ProductStockStatus,
} from '@__generated__/globalTypes';

import { hasDatePassed } from './helpers';
import IconDownload from './IconDownload';
import messages from './StockMessage.messages';
import { StockDotGreen, StockDotRed } from './styles';

export enum PACKAGE_TYPE {
  BOX = 'BOX',
  DL = 'DL',
}

type StockMessageProps = {
  packageType?: PACKAGE_TYPE | null;
  stockStatus?: Magento2_ProductStockStatus | null;
  releaseDate?: string | null;
  freeShipping?: boolean;
};

const StockMessage = ({
  packageType,
  stockStatus,
  freeShipping = false,
  releaseDate,
}: StockMessageProps) => {
  const countryCode = Cookies.get(COUNTRY_COOKIE);
  const releaseDatePassed = releaseDate && hasDatePassed(releaseDate);
  const isPreOrder = releaseDate && !releaseDatePassed;

  if (packageType === PACKAGE_TYPE.DL) {
    return (
      <Flex
        data-testid="stock-message-dl-in-stock"
        sx={{
          alignItems: 'center',
        }}
      >
        {!isPreOrder && (
          <IconDownload
            sx={{
              mr: 2,
            }}
          />
        )}
        <FormattedMessage
          {...messages[!isPreOrder ? 'downloadStock' : 'preOrder']}
          values={{
            date: (
              <FormattedDate
                value={(releaseDate && dateSafariCompatible(releaseDate)) ?? ''}
                year="numeric"
                month="long"
                day="2-digit"
              />
            ),
          }}
        />
      </Flex>
    );
  }
  if (packageType === PACKAGE_TYPE.BOX) {
    switch (stockStatus) {
      case STOCK_STATUS.IN_STOCK: {
        return (
          <div data-testid="stock-message-box-in-stock">
            {freeShipping && (
              <Box data-testid="stock-message-box-free-shipping">
                <Text sx={{ fontWeight: 500 }}>
                  <FormattedMessage {...messages.freeShipping} />:
                </Text>{' '}
                (<FormattedCountryName code={countryCode} />)
              </Box>
            )}

            <Box my={15}>
              {!isPreOrder && <StockDotGreen />}

              <FormattedMessage
                {...messages[!isPreOrder ? 'inStock' : 'preOrderHardware']}
                values={{
                  date: (
                    <FormattedDate
                      value={
                        (releaseDate && dateSafariCompatible(releaseDate)) ?? ''
                      }
                      year="numeric"
                      month="long"
                      day="2-digit"
                    />
                  ),
                }}
              />
            </Box>

            {!isPreOrder && (
              <FormattedMessage {...messages.inStockDescription} />
            )}
          </div>
        );
      }
      case STOCK_STATUS.OUT_OF_STOCK: {
        return (
          <Box my={15} data-testid="stock-message-box-out-of-stock">
            <StockDotRed />
            <FormattedMessage {...messages.outOfStock} />
          </Box>
        );
      }
      default: {
        return null;
      }
    }
  }
  return null;
};

export default StockMessage;
